import React, { useMemo, useState } from 'react';
import { Alert, Button, Chip, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, PassOut, PassService, RecurringBillingMode } from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { useApiResponseHandlerContext } from '../../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../../common/apiNotifications/ApiResponseHandlerContext';
import { getPassOrderPage, getPassOrderPages } from '../../../../utils/AccountUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { getCurrencySymbol } from '../../../../utils/utils';
import { formatSecondsToTime, getDayName } from '../../../../utils/weekdayFormat';
import ConfirmationDialog from '../../../../common/ui/ConfimationDialog';
import { getPassSwapBlock, getRecurringSubscriptionView, isRecurringPassType, RecurringAction } from '../../utils';
import { DetailRow } from '../summaryPanel/DetailRow';
import { ScheduleSelectionRow } from '../schedulePanel/ScheduleSelectionRow';
import { SwapPassDialog } from './SwapPassDialog';
import { CardOnFileSection } from './CardOnFileSection';

type Props = {
  pass: PassOut;
  onUpdated: (pass: PassOut) => void;
  onRecurringAction: (action: RecurringAction) => void;
  onPauseRequest: () => void;
};

export const SubscriptionSection = ({ pass, onUpdated, onRecurringAction, onPauseRequest }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { showResponseNotification } = useApiResponseHandlerContext();
  const dateFormat = getDateFormat(account).longDateFormat;
  const timeFormat = getTimeFormat(account);
  const currencySymbol = getCurrencySymbol(account);

  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  // Still billed by Grow's scheduler rather than our own token charges. The field was added with the
  // token switch, so memberships predating it read back null -- test for "not token", never for
  // grow_direct_debit. The recurring gate matters too: every punch pass is null as well.
  const onLegacyBilling =
    isRecurringPassType(passOrderPage?.pass_type) && pass.recurring_billing_mode !== RecurringBillingMode.TOKEN;
  const pendingSwap = pass.pending_swap;
  const pendingTarget = pendingSwap ? getPassOrderPage(account, pendingSwap.target_pass_order_page_id) : undefined;

  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [cancelSwapDialogOpen, setCancelSwapDialogOpen] = useState(false);

  const eligibleTargetsCount = useMemo(
    () =>
      getPassOrderPages(account).filter((page) => isRecurringPassType(page.pass_type) && page.id !== pass.order_page_id)
        .length,
    [account, pass.order_page_id],
  );

  const swapBlock = getPassSwapBlock(pass, passOrderPage, eligibleTargetsCount);
  const subscriptionView = getRecurringSubscriptionView(pass, passOrderPage, t);

  const labelOf = (id?: string): string => {
    const page = id ? getPassOrderPage(account, id) : undefined;
    return page?.label?.[language] || page?.label?.en || id || '';
  };

  const cancelPendingSwap = async () => {
    try {
      const updated = await PassService.cancelPendingPassSwap(pass.id);
      onUpdated(updated);
      showResponseNotification(t('passes.swap.swap_canceled_toast'), ApiResponseHandlerType.SUCCESS);
    } catch {
      showResponseNotification(t('saveHeader.errorGeneric'), ApiResponseHandlerType.ERROR);
    }
  };

  return (
    <Grid container flexDirection="column" gap={1}>
      <DetailRow label={t('passes.swap.current')} value={labelOf(pass.order_page_id)} />
      {onLegacyBilling && (
        <Grid container>
          <Chip size="small" color="default" variant="outlined" label={t('passes.recurring.legacy_engine')} />
        </Grid>
      )}
      {pass.next_charge_date && (
        <DetailRow label={t('passes.swap.next_charge_date')} value={moment(pass.next_charge_date).format(dateFormat)} />
      )}
      {pass.next_charge_amount != null && (
        <DetailRow label={t('passes.swap.next_charge_amount')} value={`${pass.next_charge_amount}${currencySymbol}`} />
      )}

      {pendingSwap && (
        <Alert
          severity="info"
          action={
            <Button size="small" onClick={() => setCancelSwapDialogOpen(true)}>
              {t('passes.swap.cancel_scheduled_cta')}
            </Button>
          }>
          {t('passes.swap.pending_banner', {
            target: labelOf(pendingSwap.target_pass_order_page_id),
            date: pendingSwap.effective_date ? moment(pendingSwap.effective_date).format(dateFormat) : '',
            price: `${pendingSwap.price}${currencySymbol}`,
          })}
          {!!pendingSwap.schedule_selections?.length && (
            <Grid container flexDirection="column" mt={1}>
              {pendingSwap.schedule_selections.map((selection) => (
                <ScheduleSelectionRow
                  key={`${selection.order_page_id}-${selection.weekday}-${selection.start_time}`}
                  weekdayLabel={getDayName(selection.weekday, language)}
                  timeLabel={formatSecondsToTime(selection.start_time, timeFormat)}
                  pageLabel={labelOf(selection.order_page_id)}
                />
              ))}
            </Grid>
          )}
        </Alert>
      )}

      {swapBlock ? (
        <Typography variant="caption" color="text.secondary">
          {t(`passes.swap.blocked.${swapBlock}`)}
        </Typography>
      ) : (
        <Grid container>
          <Button variant="outlined" onClick={() => setSwapDialogOpen(true)}>
            {t('passes.swap.change_cta')}
          </Button>
        </Grid>
      )}

      {/* getRecurringSubscriptionView yields no label and no actions for archived passes. */}
      <Grid
        container
        gap={0.5}
        flexDirection="column"
        borderRadius="8px"
        border="1px solid"
        borderColor="divider"
        p={1.5}
        mt={1}>
        <Typography fontWeight={700}>{t('calendar.edit_order.status')}</Typography>
        <Typography>{subscriptionView.label || t(`passes.status.${pass.status}`)}</Typography>
        {subscriptionView.actions.length > 0 && (
          <Grid container gap={1} mt={1}>
            {subscriptionView.actions.map((action) => (
              <Button
                key={action}
                variant="outlined"
                color={action === RecurringAction.Cancel ? 'warning' : 'primary'}
                onClick={() =>
                  action === RecurringAction.Pause || action === RecurringAction.EditPause
                    ? onPauseRequest()
                    : onRecurringAction(action)
                }>
                {t(`passes.recurring.${action}`)}
              </Button>
            ))}
          </Grid>
        )}
      </Grid>

      <CardOnFileSection pass={pass} passOrderPage={passOrderPage} />

      <Divider sx={{ my: 1 }} />

      {passOrderPage && (
        <SwapPassDialog
          open={swapDialogOpen}
          onClose={() => setSwapDialogOpen(false)}
          pass={pass}
          currentPassOrderPage={passOrderPage}
          onUpdated={onUpdated}
        />
      )}
      <ConfirmationDialog
        title={t('passes.swap.cancel_scheduled_cta')}
        content={
          <Typography>
            {t('passes.swap.cancel_scheduled_confirm', { target: pendingTarget ? labelOf(pendingTarget.id) : '' })}
          </Typography>
        }
        ctaText={t('passes.swap.cancel_scheduled_cta')}
        confirmButtonColor="warning"
        isOpen={cancelSwapDialogOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed) await cancelPendingSwap();
          setCancelSwapDialogOpen(false);
        }}
      />
    </Grid>
  );
};
